import { Dropdown, Tooltip } from 'antd';
import { RevxDate, SHIPPING_DATE_FORMAT } from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxOverlay } from 'components/RevxOverlay';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import { Text, TextHeavy, UnderlineText } from 'components/RevxTypography';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { JobCardShippingItem } from 'domains/workspaceDomain';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { SHIPPING_WILL_CALL } from '../../utils/constants';
import './index.less';

export type RevxShippingInfoProps = {
  shippingItems: JobCardShippingItem[];
  displayItems?: {
    label: string;
    valueProp: keyof JobCardShippingItem;
  }[];
};
export type RevxShippingItemProps = {
  item: JobCardShippingItem;
  valueProp?: keyof JobCardShippingItem;
  label?: string;
};

export const RevxShippingItem = ({
  item,
  valueProp,
  label,
}: RevxShippingItemProps) => {
  const { t } = useTranslation();

  if (valueProp && !item[valueProp]) {
    return null;
  }
  const { carrierService, carrierType, shippingDetail, actualShipTime } = item;
  const isNonstandard = carrierType === CarrierTypeEnum.Nonstandard;
  const isWillCall = carrierService === SHIPPING_WILL_CALL;
  const nonstandardLabel = () => {
    if (isNonstandard) {
      if (label === t('workspace.list.dateShipped')) {
        if (isWillCall) {
          return t('workspace.list.datePickedUp');
        } else {
          return t('workspace.list.shipDate');
        }
      } else if (label === t('workspace.list.shippingCarrier')) {
        if (actualShipTime) {
          if (isWillCall) {
            return t('workspace.list.shippingInfo');
          } else {
            return t('workspace.list.shippingDetails');
          }
        }
        return false;
      }
    }
    return label;
  };
  const nonstandardValue = () => {
    if (isNonstandard) {
      if (label === t('workspace.list.dateShipped')) {
        return (
          <RevxDate
            dateStr={
              isWillCall
                ? item.actualDeliveryTime || item.estimatedDeliveryTime
                : item.actualShipTime
            }
            formatStr={SHIPPING_DATE_FORMAT}
          />
        );
      } else if (label === t('workspace.list.shippingCarrier')) {
        if (actualShipTime) {
          return isWillCall ? (
            SHIPPING_WILL_CALL
          ) : (
            <Tooltip title={shippingDetail}>
              <div className="shipping-details-wrap">
                {shippingDetail || '--'}
              </div>
            </Tooltip>
          );
        }
        return false;
      }
    }
    return (
      <>
        {valueProp && !valueProp.includes('Time') ? (
          valueProp === 'trackingCode' ? (
            <RevxTrackingInfo
              carrierType={carrierType}
              value={item[valueProp]}
            />
          ) : (
            item[valueProp]
          )
        ) : (
          <>
            {moment(
              valueProp
                ? (item[valueProp] as string)
                : item.actualDeliveryTime || item.estimatedDeliveryTime
            ).format('ddd')}
            {`, `}
            <RevxDate
              dateStr={
                valueProp
                  ? (item[valueProp] as string)
                  : item.actualDeliveryTime || item.estimatedDeliveryTime
              }
              formatStr={SHIPPING_DATE_FORMAT}
            />
          </>
        )}
      </>
    );
  };
  return (
    <RevxFlexLayout className="revx-shipping-info-item">
      {nonstandardLabel() && (
        <TextHeavy>
          {nonstandardLabel() ||
            (!item.actualDeliveryTime
              ? t('orderHistory.estimatedArrivalDate')
              : t('orderHistory.arrivalDate'))}
          :
        </TextHeavy>
      )}
      {nonstandardValue() && (
        <Text
          style={{
            margin: '0 0 0 8px',
          }}
        >
          {nonstandardValue()}
        </Text>
      )}
    </RevxFlexLayout>
  );
};

export const RevxShippingInfo = ({
  shippingItems,
  displayItems,
}: RevxShippingInfoProps) => {
  const { t } = useTranslation();
  return (
    <>
      {shippingItems?.length > 0 && (
        <div>
          {displayItems && displayItems.length > 0 ? (
            displayItems.map((displayItem) => {
              return (
                <RevxShippingItem
                  key={displayItem.valueProp}
                  item={shippingItems[0]}
                  label={displayItem?.label}
                  valueProp={displayItem?.valueProp}
                />
              );
            })
          ) : (
            <RevxShippingItem item={shippingItems[0]} />
          )}
        </div>
      )}
      {shippingItems?.length > 1 && (
        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          overlay={
            <RevxOverlay>
              {shippingItems.slice(1).map((shippingItem, index) => {
                return (
                  <div key={index}>
                    {displayItems && displayItems.length > 0 ? (
                      displayItems.map((displayItem) => {
                        return (
                          <RevxShippingItem
                            key={displayItem.valueProp}
                            item={shippingItem}
                            label={displayItem?.label}
                            valueProp={displayItem?.valueProp}
                          />
                        );
                      })
                    ) : (
                      <RevxShippingItem item={shippingItem} />
                    )}
                  </div>
                );
              })}
            </RevxOverlay>
          }
        >
          <UnderlineText
            color="blue"
            size="small"
            style={{
              margin: '0 0 0 8px',
            }}
          >
            {shippingItems.length - 1} {t('orderHistory.moreShipping')}
          </UnderlineText>
        </Dropdown>
      )}
    </>
  );
};
